import type { OrderCancellationCode } from "@loot-os/core/db/schema";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  cancellationReasonError,
  customerMessageError,
  FULFILLER_CANCELLATION_CODES,
  type FulfillerCancellationCode,
  parseCancellationBonus,
} from "../lib/orderActions";
import { useTRPC } from "../lib/trpc";

const CODE_LABELS: Record<FulfillerCancellationCode, string> = {
  PRICE_EXCEEDED: "PRICE_EXCEEDED — the live buy price is above the snapshot",
  OUT_OF_STOCK: "OUT_OF_STOCK — the item or the shopper's variant is gone",
};

/**
 * The one action with required input: a coded cause a tenant branches on and
 * the fulfiller's own note, plus the cancel plan the tenant that owns the order
 * applies on its side (Retool's message, Skip Email and marketplace bonus).
 * Cancelling a line instead of the order is the same form against
 * orders.cancelLine, so both live here.
 */
export function OrderCancelDialog({
  orderId,
  orderType,
  line,
}: {
  orderId: string;
  /** The bonus field shows on a MARKETPLACE order only, the one type the server accepts it on. */
  orderType: string;
  /** Set to cancel one line; the order itself is cancelled when this is absent. */
  line?: { id: string; isLastActive: boolean };
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<FulfillerCancellationCode>("PRICE_EXCEEDED");
  const [reason, setReason] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [bonusText, setBonusText] = useState("");
  const offersBonus = orderType === "MARKETPLACE";
  // TCG Fulfill Order's "Cancel Card"; any other kind of order cancels a line.
  const lineNoun =
    orderType === "TCG"
      ? { label: "Cancel Card", word: "card" }
      : { label: "Cancel line", word: "line" };

  const done = (message: string) => {
    toast.success(message);
    void queryClient.invalidateQueries({ queryKey: trpc.platform.fulfillment.orders.pathKey() });
    setOpen(false);
    setReason("");
    setCustomerMessage("");
    setNotifyCustomer(true);
    setBonusText("");
  };

  const cancelOrder = useMutation(
    trpc.platform.fulfillment.orders.cancel.mutationOptions({
      onSuccess: (result) =>
        done(
          "shippingFeeRefunded" in result
            ? "Order cancelled and the shipping fee refunded."
            : "Order cancelled.",
        ),
    }),
  );
  const cancelLine = useMutation(
    trpc.platform.fulfillment.orders.cancelLine.mutationOptions({
      onSuccess: () => done(`${lineNoun.word === "card" ? "Card" : "Line"} cancelled.`),
    }),
  );

  const isPending = cancelOrder.isPending || cancelLine.isPending;
  const reasonError = cancellationReasonError(reason);
  const messageError = customerMessageError(customerMessage);
  const bonus = offersBonus ? parseCancellationBonus(bonusText) : { bonusPercent: undefined };
  const bonusError = "error" in bonus ? bonus.error : undefined;
  const formError = reasonError ?? messageError ?? bonusError;

  const submit = () => {
    if (formError || "error" in bonus) return;
    // The dashboard never imports the schema module, so the picker holds the enum's values as plain strings.
    const input = {
      id: orderId,
      code: code as OrderCancellationCode,
      reason: reason.trim(),
      customerMessage: customerMessage.trim() || undefined,
      notifyCustomer,
      bonusPercent: bonus.bonusPercent,
    };
    if (line) cancelLine.mutate({ ...input, lineId: line.id });
    else cancelOrder.mutate(input);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && setOpen(next)}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant={line ? "ghost" : "destructive"}
            size={line ? "sm" : undefined}
          />
        }
      >
        {line ? lineNoun.label : "Cancel Order"}
      </DialogTrigger>
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>{line ? `Cancel this ${lineNoun.word}?` : "Cancel this order?"}</DialogTitle>
          <DialogDescription>
            The tenant reads the code back on the order; the note is for whoever looks at it later.
            The tenant that owns the order sends the customer the message below, or its own default.
            {line?.isLastActive
              ? ` This is the last ${lineNoun.word} still active, so cancelling it cancels the whole order.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="cancellation-code">Cause</Label>
            <Select
              value={code}
              disabled={isPending}
              onValueChange={(next) => setCode(next as FulfillerCancellationCode)}
            >
              <SelectTrigger id="cancellation-code">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FULFILLER_CANCELLATION_CODES.map((member) => (
                  <SelectItem key={member} value={member}>
                    {CODE_LABELS[member]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cancellation-reason">Note</Label>
            <Textarea
              id="cancellation-reason"
              value={reason}
              disabled={isPending}
              aria-invalid={reasonError !== undefined}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonError ? <p className="text-sm text-destructive">{reasonError}</p> : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cancellation-customer-message">
              Message to the customer (optional)
            </Label>
            <Textarea
              id="cancellation-customer-message"
              value={customerMessage}
              disabled={isPending}
              aria-invalid={messageError !== undefined}
              onChange={(e) => setCustomerMessage(e.target.value)}
            />
            {messageError ? <p className="text-sm text-destructive">{messageError}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="cancellation-notify-customer"
              checked={notifyCustomer}
              disabled={isPending}
              onCheckedChange={(checked) => setNotifyCustomer(checked === true)}
            />
            <Label htmlFor="cancellation-notify-customer">Email the customer</Label>
          </div>
          {offersBonus ? (
            <div className="grid gap-2">
              <Label htmlFor="cancellation-bonus">Cancellation bonus % (optional)</Label>
              <Input
                id="cancellation-bonus"
                inputMode="decimal"
                value={bonusText}
                disabled={isPending}
                aria-invalid={bonusError !== undefined}
                onChange={(e) => setBonusText(e.target.value)}
              />
              {bonusError ? <p className="text-sm text-destructive">{bonusError}</p> : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => setOpen(false)}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isPending || formError !== undefined}
            >
              {isPending ? "Cancelling…" : line ? lineNoun.label : "Cancel Order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
